import { create } from 'zustand';
import { getCurrentTimetable, mockTimetables } from '../lib/mockData/timetables';
import type { Lesson, Timetable, TimetableVersion } from '../types';

interface VersionComparison {
  versionA: string;
  versionB: string;
  differences: {
    lessonsAdded: number;
    lessonsRemoved: number;
    lessonsModified: number;
    conflictDelta: number;
    coverageDelta: number;
  };
}

interface TimetableState {
  // Current Timetable
  currentTimetable: Timetable | null;
  setCurrentTimetable: (timetable: Timetable) => void;
  loadCurrentTimetable: () => void;

  // All Timetables
  timetables: Timetable[];
  addTimetable: (timetable: Timetable) => void;
  updateTimetable: (id: string, data: Partial<Timetable>) => void;
  deleteTimetable: (id: string) => void;
  getTimetableById: (id: string) => Timetable | undefined;

  // Lesson Operations
  addLesson: (lesson: Lesson) => void;
  updateLesson: (id: string, data: Partial<Lesson>) => void;
  deleteLesson: (id: string) => void;
  moveLessonToSlot: (lessonId: string, day: Lesson['day'], period: number) => void;

  // Finalization
  finalizeTimetable: (id: string) => void;
  finalizeYearGroup: (timetableId: string, yearGroupId: string) => void;
  unfinalizeYearGroup: (timetableId: string, yearGroupId: string) => void;
  isYearGroupFinalized: (timetableId: string, yearGroupId: string) => boolean;
  getFinalizedYearGroups: (timetableId: string) => string[];

  // Version Control
  versions: TimetableVersion[];
  createNewVersion: (timetableId: string, notes?: string) => TimetableVersion | null;
  getVersionHistory: (timetableId: string) => TimetableVersion[];
  restoreVersion: (versionId: string) => Timetable | null;
  compareVersions: (versionAId: string, versionBId: string) => VersionComparison | null;
  archiveVersion: (versionId: string) => void;
  getLatestVersion: (timetableId: string) => TimetableVersion | null;

  // Rollback
  rollbackToVersion: (timetableId: string, version: number) => boolean;
  canRollback: (timetableId: string) => boolean;

  // Filters & Views
  filterByDay: (day: Lesson['day']) => Lesson[];
  filterByPeriod: (period: number) => Lesson[];
  filterByTeacher: (teacherId: string) => Lesson[];
  filterByRoom: (roomId: string) => Lesson[];
  filterByYearGroup: (yearGroupId: string) => Lesson[];

  // Drag-and-Drop Validation
  validateLessonMove: (lessonId: string, day: Lesson['day'], period: number) => { valid: boolean; conflicts: string[] };
  getSlotConflicts: (day: Lesson['day'], period: number) => string[];
}

// Generate initial versions from mock timetables
const initialVersions: TimetableVersion[] = mockTimetables.map((t) => ({
  id: `${t.id}-v${t.version}`,
  timetableId: t.id,
  version: t.version,
  timestamp: t.createdAt,
  createdBy: t.createdBy,
  notes: t.status === 'finalized' ? 'Finalized version' : `Version ${t.version} created`,
  lessons: t.lessons,
  stats: t.stats,
}));

export const useTimetableStore = create<TimetableState>((set, get) => ({
  // Initial State
  currentTimetable: getCurrentTimetable() || null,
  timetables: mockTimetables,
  versions: initialVersions,

  setCurrentTimetable: (timetable) =>
    set(() => ({
      currentTimetable: timetable,
    })),

  loadCurrentTimetable: () => {
    const current = getCurrentTimetable();
    set(() => ({
      currentTimetable: current || null,
    }));
  },

  // Timetable Operations
  addTimetable: (timetable) =>
    set((state) => ({
      timetables: [...state.timetables, timetable],
    })),

  updateTimetable: (id, data) =>
    set((state) => {
      const updatedTimetables = state.timetables.map((t) =>
        t.id === id ? { ...t, ...data, lastModified: new Date().toISOString() } : t,
      );
      return {
        timetables: updatedTimetables,
        currentTimetable: state.currentTimetable?.id === id ? { ...state.currentTimetable, ...data } : state.currentTimetable,
      };
    }),

  deleteTimetable: (id) =>
    set((state) => ({
      timetables: state.timetables.filter((t) => t.id !== id),
      currentTimetable: state.currentTimetable?.id === id ? null : state.currentTimetable,
    })),

  getTimetableById: (id) => {
    return get().timetables.find((t) => t.id === id);
  },

  // Lesson Operations
  addLesson: (lesson) =>
    set((state) => {
      if (!state.currentTimetable) return state;
      const updatedLessons = [...state.currentTimetable.lessons, lesson];
      const updatedStats = {
        ...state.currentTimetable.stats,
        totalLessons: updatedLessons.length,
      };
      return {
        currentTimetable: {
          ...state.currentTimetable,
          lessons: updatedLessons,
          stats: updatedStats,
        },
      };
    }),

  updateLesson: (id, data) =>
    set((state) => {
      if (!state.currentTimetable) return state;
      const updatedLessons = state.currentTimetable.lessons.map((l) => (l.id === id ? { ...l, ...data } : l));
      return {
        currentTimetable: {
          ...state.currentTimetable,
          lessons: updatedLessons,
        },
      };
    }),

  deleteLesson: (id) =>
    set((state) => {
      if (!state.currentTimetable) return state;
      const updatedLessons = state.currentTimetable.lessons.filter((l) => l.id !== id);
      const updatedStats = {
        ...state.currentTimetable.stats,
        totalLessons: updatedLessons.length,
      };
      return {
        currentTimetable: {
          ...state.currentTimetable,
          lessons: updatedLessons,
          stats: updatedStats,
        },
      };
    }),

  moveLessonToSlot: (lessonId, day, period) =>
    set((state) => {
      if (!state.currentTimetable) return state;
      const updatedLessons = state.currentTimetable.lessons.map((l) =>
        l.id === lessonId ? { ...l, day, period } : l,
      );
      return {
        currentTimetable: {
          ...state.currentTimetable,
          lessons: updatedLessons,
        },
      };
    }),

  // Finalization
  finalizeTimetable: (id) =>
    set((state) => ({
      timetables: state.timetables.map((t) =>
        t.id === id
          ? {
              ...t,
              isFinalized: true,
              status: 'finalized',
              finalizedYearGroups: ['year-7', 'year-8', 'year-9', 'year-10', 'year-11', 'year-12', 'year-13'],
            }
          : t,
      ),
    })),

  finalizeYearGroup: (timetableId, yearGroupId) =>
    set((state) => ({
      timetables: state.timetables.map((t) =>
        t.id === timetableId && !t.finalizedYearGroups.includes(yearGroupId)
          ? {
              ...t,
              finalizedYearGroups: [...t.finalizedYearGroups, yearGroupId],
            }
          : t,
      ),
    })),

  unfinalizeYearGroup: (timetableId, yearGroupId) =>
    set((state) => ({
      timetables: state.timetables.map((t) =>
        t.id === timetableId
          ? {
              ...t,
              finalizedYearGroups: t.finalizedYearGroups.filter((yg) => yg !== yearGroupId),
              isFinalized: false,
              status: 'generated',
            }
          : t,
      ),
    })),

  isYearGroupFinalized: (timetableId, yearGroupId) => {
    const timetable = get().timetables.find((t) => t.id === timetableId);
    return timetable?.finalizedYearGroups.includes(yearGroupId) ?? false;
  },

  getFinalizedYearGroups: (timetableId) => {
    const timetable = get().timetables.find((t) => t.id === timetableId);
    return timetable?.finalizedYearGroups ?? [];
  },

  // Version Control
  createNewVersion: (timetableId, notes) => {
    const { timetables, versions } = get();
    const timetable = timetables.find((t) => t.id === timetableId);
    if (!timetable) return null;

    const existingVersions = versions.filter((v) => v.timetableId === timetableId);
    const maxVersion = existingVersions.length > 0 ? Math.max(...existingVersions.map((v) => v.version)) : 0;
    const newVersionNumber = maxVersion + 1;

    const newVersion: TimetableVersion = {
      id: `${timetableId}-v${newVersionNumber}`,
      timetableId,
      version: newVersionNumber,
      timestamp: new Date().toISOString(),
      createdBy: timetable.createdBy,
      notes: notes || `Version ${newVersionNumber} created`,
      lessons: [...timetable.lessons],
      stats: { ...timetable.stats },
      changesFromPrevious:
        existingVersions.length > 0
          ? {
              lessonsAdded: 0,
              lessonsRemoved: 0,
              lessonsModified: 0,
              conflictDelta: 0,
            }
          : undefined,
    };

    set((state) => ({
      versions: [...state.versions, newVersion],
      timetables: state.timetables.map((t) =>
        t.id === timetableId
          ? { ...t, version: newVersionNumber, lastModified: new Date().toISOString() }
          : t,
      ),
    }));

    return newVersion;
  },

  getVersionHistory: (timetableId) => {
    return get()
      .versions.filter((v) => v.timetableId === timetableId)
      .sort((a, b) => b.version - a.version);
  },

  restoreVersion: (versionId) => {
    const { versions, timetables } = get();
    const version = versions.find((v) => v.id === versionId);
    if (!version) return null;

    const timetable = timetables.find((t) => t.id === version.timetableId);
    if (!timetable) return null;

    const restoredTimetable: Timetable = {
      ...timetable,
      lessons: [...version.lessons],
      stats: { ...version.stats },
      lastModified: new Date().toISOString(),
      status: 'draft',
    };

    set((state) => ({
      timetables: state.timetables.map((t) => (t.id === version.timetableId ? restoredTimetable : t)),
      currentTimetable: state.currentTimetable?.id === version.timetableId ? restoredTimetable : state.currentTimetable,
    }));

    // Create a new version to track the restoration
    get().createNewVersion(version.timetableId, `Restored from version ${version.version}`);

    return restoredTimetable;
  },

  compareVersions: (versionAId, versionBId) => {
    const { versions } = get();
    const versionA = versions.find((v) => v.id === versionAId);
    const versionB = versions.find((v) => v.id === versionBId);

    if (!versionA || !versionB) return null;

    const lessonIdsA = new Set(versionA.lessons.map((l) => l.id));
    const lessonIdsB = new Set(versionB.lessons.map((l) => l.id));

    const lessonsAdded = versionB.lessons.filter((l) => !lessonIdsA.has(l.id)).length;
    const lessonsRemoved = versionA.lessons.filter((l) => !lessonIdsB.has(l.id)).length;

    // Count modified lessons (same ID but different content)
    let lessonsModified = 0;
    for (const lessonA of versionA.lessons) {
      const lessonB = versionB.lessons.find((l) => l.id === lessonA.id);
      if (lessonB) {
        if (
          lessonA.day !== lessonB.day ||
          lessonA.period !== lessonB.period ||
          lessonA.teacherId !== lessonB.teacherId ||
          lessonA.roomId !== lessonB.roomId
        ) {
          lessonsModified++;
        }
      }
    }

    return {
      versionA: versionAId,
      versionB: versionBId,
      differences: {
        lessonsAdded,
        lessonsRemoved,
        lessonsModified,
        conflictDelta: versionB.stats.conflictCount - versionA.stats.conflictCount,
        coverageDelta: versionB.stats.curriculumCoverage - versionA.stats.curriculumCoverage,
      },
    };
  },

  archiveVersion: (versionId) => {
    // In a real app, this would mark version as archived
    // For now, we just remove it from active versions
    set((state) => ({
      versions: state.versions.filter((v) => v.id !== versionId),
    }));
  },

  getLatestVersion: (timetableId) => {
    const versions = get().getVersionHistory(timetableId);
    return versions.length > 0 ? versions[0] : null;
  },

  // Rollback
  rollbackToVersion: (timetableId, version) => {
    const versions = get().getVersionHistory(timetableId);
    const targetVersion = versions.find((v) => v.version === version);
    if (!targetVersion) return false;

    get().restoreVersion(targetVersion.id);
    return true;
  },

  canRollback: (timetableId) => {
    const versions = get().getVersionHistory(timetableId);
    return versions.length > 1;
  },

  // Filters & Views
  filterByDay: (day) => {
    const { currentTimetable } = get();
    if (!currentTimetable) return [];
    return currentTimetable.lessons.filter((l) => l.day === day);
  },

  filterByPeriod: (period) => {
    const { currentTimetable } = get();
    if (!currentTimetable) return [];
    return currentTimetable.lessons.filter((l) => l.period === period);
  },

  filterByTeacher: (teacherId) => {
    const { currentTimetable } = get();
    if (!currentTimetable) return [];
    return currentTimetable.lessons.filter((l) => l.teacherId === teacherId);
  },

  filterByRoom: (roomId) => {
    const { currentTimetable } = get();
    if (!currentTimetable) return [];
    return currentTimetable.lessons.filter((l) => l.roomId === roomId);
  },

  filterByYearGroup: (yearGroupId) => {
    const { currentTimetable } = get();
    if (!currentTimetable) return [];
    return currentTimetable.lessons.filter((l) => l.studentGroupId.includes(yearGroupId));
  },

  // Drag-and-Drop Validation
  validateLessonMove: (lessonId, day, period) => {
    const { currentTimetable } = get();
    if (!currentTimetable) return { valid: true, conflicts: [] };

    const lesson = currentTimetable.lessons.find((l) => l.id === lessonId);
    if (!lesson) return { valid: false, conflicts: ['Lesson not found'] };

    // Get all lessons in the target slot (excluding the one being moved)
    const lessonsInSlot = currentTimetable.lessons.filter(
      (l) => l.day === day && l.period === period && l.id !== lessonId,
    );

    const conflicts: string[] = [];

    // Check for teacher conflicts
    const teacherConflict = lessonsInSlot.find((l) => l.teacherId === lesson.teacherId);
    if (teacherConflict) {
      conflicts.push(`Teacher ${lesson.teacherName} is already teaching ${teacherConflict.subjectName}`);
    }

    // Check for room conflicts
    const roomConflict = lessonsInSlot.find((l) => l.roomId === lesson.roomId);
    if (roomConflict) {
      conflicts.push(`Room ${lesson.roomName} is already occupied by ${roomConflict.subjectName}`);
    }

    // Check for student group conflicts
    const groupConflict = lessonsInSlot.find((l) => l.studentGroupId === lesson.studentGroupId);
    if (groupConflict) {
      conflicts.push(`Group ${lesson.groupName} is already assigned to ${groupConflict.subjectName}`);
    }

    return {
      valid: conflicts.length === 0,
      conflicts,
    };
  },

  getSlotConflicts: (day, period) => {
    const { currentTimetable } = get();
    if (!currentTimetable) return [];

    const lessonsInSlot = currentTimetable.lessons.filter((l) => l.day === day && l.period === period);

    if (lessonsInSlot.length <= 1) return [];

    const conflicts: string[] = [];
    const teachers = new Set<string>();
    const rooms = new Set<string>();
    const groups = new Set<string>();

    for (const lesson of lessonsInSlot) {
      if (teachers.has(lesson.teacherId)) {
        conflicts.push(`Teacher ${lesson.teacherName} double-booked`);
      }
      teachers.add(lesson.teacherId);

      if (rooms.has(lesson.roomId)) {
        conflicts.push(`Room ${lesson.roomName} double-booked`);
      }
      rooms.add(lesson.roomId);

      if (groups.has(lesson.studentGroupId)) {
        conflicts.push(`Group ${lesson.groupName} double-booked`);
      }
      groups.add(lesson.studentGroupId);
    }

    return conflicts;
  },
}));
